import * as React from "react";
import {IconButton, Link as MuiLink, TableCell, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FavoriteIcon from "@mui/icons-material/Favorite";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SchoolIcon from "@mui/icons-material/School";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseIcon from "@mui/icons-material/Pause";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import ShieldIcon from "@mui/icons-material/Shield";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import BlockIcon from "@mui/icons-material/Block";
import StripedTableRow from "../../components/StripedTableRow";

export const EXPAND_CELL = {width: 26, minWidth: 26, textAlign: "center"};

function money(n: number, digits = 2) {
    return "$" + (n || 0).toLocaleString(undefined, {minimumFractionDigits: digits, maximumFractionDigits: digits});
}

function gradeColor(g: string) {
    if (g === "A") {
        return "green";
    }
    if (g === "B") {
        return "red";
    }
    return "blue";
}

const KEEP_BG = "pr-keep-bg";

function keepCell(sx: object | undefined) {
    return sx ? {className: KEEP_BG, sx} : {};
}

function capSx(isToday: boolean, cap: number, used: number) {
    if (isToday && (cap === 0 || cap <= used)) {
        return {bgcolor: "#f2dede"};
    }
    if (isToday && cap > 0 && used / cap >= 0.9 && used / cap < 1) {
        return {bgcolor: "#fcf8e3"};
    }
    return undefined;
}

function monthCapSx(cap: number, used: number) {
    if (cap === 0 || cap <= used) {
        return {bgcolor: "#f2dede"};
    }
    if (cap > 0 && used / cap >= 0.9 && used / cap < 1) {
        return {bgcolor: "#fcf8e3"};
    }
    return undefined;
}

function icons(row: any) {
    return (
        <span>
            {row.redirectType === "Linked" && <Tooltip title="Linkout Campaign"><LinkIcon sx={{fontSize: 16, color: "green", ml: 0.3}}/></Tooltip>}
            {row.dupeChecker?.on && <Tooltip title="Dupe Active"><ContentCopyIcon sx={{fontSize: 16, color: "orange", ml: 0.3}}/></Tooltip>}
            {row.scoreChecker?.on && <Tooltip title="Score Active"><EmojiEventsIcon sx={{fontSize: 16, color: "purple", ml: 0.3}}/></Tooltip>}
            {row.isExclusive && !row.isExclusiveOnly && !row.isExclusiveTemp && <Tooltip title="Exclusive"><WarningAmberIcon sx={{fontSize: 16, color: "red", ml: 0.3}}/></Tooltip>}
            {row.isExclusiveTemp && (
                <Tooltip title="Exclusive Paused for Tier 1">
                    <Box component="span" sx={{position: "relative", display: "inline-flex", width: 18, height: 18, ml: 0.3, verticalAlign: "middle"}}>
                        <WarningAmberIcon sx={{fontSize: 16, position: "absolute"}}/>
                        <BlockIcon sx={{fontSize: 18, color: "tomato", position: "absolute"}}/>
                    </Box>
                </Tooltip>
            )}
            {row.isExclusive && row.isExclusiveOnly && <Tooltip title="Exclusive Only"><FavoriteIcon sx={{fontSize: 16, color: "red", ml: 0.3}}/></Tooltip>}
            {row.previewUrl && <Tooltip title="Preview Link"><MuiLink href={row.previewUrl} target="_blank" rel="noreferrer"><OpenInNewIcon sx={{fontSize: 14, ml: 0.3}}/></MuiLink></Tooltip>}
            {!!row.whenStart?.length && <b> {row.whenStart.join(",")}</b>}
        </span>
    );
}

function extraLinks(pm: number | null | undefined, eventId: number | null | undefined, confirmsId: number | null | undefined, pmTitle: string, eventTitle: string) {
    return (
        <>
            {pm ? <Tooltip title={pmTitle}><MuiLink href={`/programSets/${pm}`} target="_blank" rel="noreferrer" sx={{color: "text.secondary", display: "inline-flex"}}><SchoolIcon sx={{fontSize: 16}}/></MuiLink></Tooltip> : null}
            {eventId ? <Tooltip title={eventTitle}><MuiLink href={`/events/${eventId}`} target="_blank" rel="noreferrer" sx={{color: "text.secondary", display: "inline-flex"}}><EventIcon sx={{fontSize: 16}}/></MuiLink></Tooltip> : null}
            {confirmsId ? <Tooltip title="See Confirms"><MuiLink href={`/campaignConfirms/${confirmsId}`} target="_blank" rel="noreferrer" sx={{color: "text.secondary", display: "inline-flex"}}><CheckCircleOutlineIcon sx={{fontSize: 16}}/></MuiLink></Tooltip> : null}
        </>
    );
}

type RowProps = {
    row: any;
    italic?: boolean;
    open?: boolean;
    isAdmin: boolean;
    canShowMonthly: boolean;
    isToday: boolean;
    onToggle?: (key: string) => void;
    onMenu?: (row: any, el: HTMLElement) => void;
    onSkip?: (hpCampaignId: number) => void;
};

const ROW_SX = {
    italic: {fontStyle: "italic", [`&:hover .MuiTableCell-root:not(.${KEEP_BG})`]: {backgroundColor: "#e3f2fd"}},
    normal: {[`&:hover .MuiTableCell-root:not(.${KEEP_BG})`]: {backgroundColor: "#e3f2fd"}},
};
const ID_WRAP = {display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 0.5, rowGap: 0};
const ICON_GROUP = {display: "inline-flex", flexWrap: "nowrap", alignItems: "center", gap: 0.25, whiteSpace: "nowrap"};

const PrCampaignRow = React.memo(function PrCampaignRow({
    row,
    italic,
    open,
    isAdmin,
    canShowMonthly,
    isToday,
    onToggle,
    onMenu,
    onSkip,
}: RowProps) {
    return (
        <StripedTableRow sx={italic ? ROW_SX.italic : ROW_SX.normal}>
            <TableCell sx={EXPAND_CELL}>
                {row.group && (
                    <IconButton size="small" sx={{p: 0}} onClick={() => onToggle?.(row.key)}>
                        {open ? <IndeterminateCheckBoxOutlinedIcon sx={{fontSize: 18}}/> : <AddBoxOutlinedIcon sx={{fontSize: 18}}/>}
                    </IconButton>
                )}
            </TableCell>
            <TableCell>{row.companyName}</TableCell>
            <TableCell>{row.brokerName}</TableCell>
            <TableCell {...keepCell(row.isBranded ? {bgcolor: "#333", color: "white"} : undefined)}>{row.schoolName}</TableCell>
            <TableCell>
                {!row.group && <>{row.appId}{isAdmin && icons(row)}</>}
            </TableCell>
            <TableCell {...keepCell(!row.group && !row.campaignActive ? {bgcolor: "#f2dede"} : undefined)}>
                {!row.group && (isAdmin
                    ? <Box sx={ID_WRAP}>
                        <MuiLink href={`/campaigns/${row.hpCampaignId}`} target="_blank" rel="noreferrer" sx={{whiteSpace: "nowrap"}}>{row.hpCampaignId}</MuiLink>
                        <Box sx={ICON_GROUP}>
                            {extraLinks(row.hpCampusSearchId, row.hpEventIdSf, row.hpCampaignId, "Manage PM SF", "Manage Event SF")}
                            {row.hasActiveGC && <MuiLink href={`/groupCaps?s=${row.hpCampaignId}&a=1`} target="_blank" rel="noreferrer" sx={{bgcolor: "green", color: "white", px: 0.5, borderRadius: 1, fontSize: 11, lineHeight: 1.4}}>GC</MuiLink>}
                            {row.hasTriggeredGC && <Tooltip title="Triggered Group Cap"><PanToolOutlinedIcon sx={{color: "red", fontSize: 16}}/></Tooltip>}
                            {row.hasPausedGC && <Tooltip title="Paused Group Cap"><PauseIcon sx={{color: "red", fontSize: 16}}/></Tooltip>}
                            {row.hasInactiveGC && <MuiLink href={`/groupCaps?s=${row.hpCampaignId}&a=0`} target="_blank" rel="noreferrer" sx={{bgcolor: "pink", color: "white", px: 0.5, borderRadius: 1, fontSize: 11, lineHeight: 1.4}}>GC</MuiLink>}
                        </Box>
                    </Box>
                    : row.hpCampaignId)}
            </TableCell>
            {isAdmin && (
                <TableCell>
                    {!row.group && (row.hpCampaignIdLb || row.hpCampaignIdLbTemp) && (
                        <Box sx={ID_WRAP}>
                            <MuiLink href={`/campaigns/${row.hpCampaignIdLb || row.hpCampaignIdLbTemp}`} target="_blank" rel="noreferrer" sx={{whiteSpace: "nowrap"}}>
                                {row.hpCampaignIdLb || row.hpCampaignIdLbTemp}
                            </MuiLink>
                            <Box sx={ICON_GROUP}>
                                {extraLinks(row.hpCampusSearchIdLb, row.hpEventIdLb, row.hpCampaignIdLb || row.hpCampaignIdLbTemp, "Manage PM LB", "Manage Event LB")}
                                {row.hpCampaignIdLbTemp ? <Tooltip title="LB Is Paused"><PauseIcon sx={{color: "red", fontSize: 22}}/></Tooltip> : null}
                                {row.dailyLbMatchPercent ? <Tooltip title="This campaign has a Max Daily LB Match Percent"><ShieldIcon sx={{color: "green", fontSize: 16}}/></Tooltip> : null}
                            </Box>
                        </Box>
                    )}
                </TableCell>
            )}
            <TableCell align="right">{!row.group ? money(row.cplSf) : ""}</TableCell>
            {isAdmin && <TableCell align="right">{!row.group && row.hpCampaignIdLb ? money(row.cplLb) : ""}</TableCell>}
            {isAdmin && <TableCell align="center">{!row.group && <span style={{color: gradeColor(row.grade)}}>{row.grade}</span>}</TableCell>}
            {isAdmin && <TableCell align="center">{!row.group && row.hpCampaignIdLb && <span style={{color: gradeColor(row.gradeLb)}}>{row.gradeLb}</span>}</TableCell>}
            {isAdmin && (
                <TableCell align="right">
                    {!row.group && row.impressions > 0 && (
                        <ZoomInIcon sx={{fontSize: 16, cursor: "pointer", mr: 0.5}} onClick={() => onSkip?.(row.hpCampaignId)}/>
                    )}
                    {(row.impressions || 0).toLocaleString()}
                </TableCell>
            )}
            {isAdmin && <TableCell align="right">{(row.leads || 0).toLocaleString()}</TableCell>}
            {isAdmin && <TableCell align="right">{(row.leadsLb || 0).toLocaleString()}</TableCell>}
            <TableCell align="right">{money(row.revenue, 0)}</TableCell>
            {isAdmin && <TableCell align="right" {...keepCell(row.ctr < 30 && row.impressions > 1 ? {bgcolor: "orange"} : undefined)}>{(row.ctr || 0).toFixed(1)}%</TableCell>}
            {isAdmin && <TableCell align="right" {...keepCell(row.autoPpi ? {bgcolor: "warning.light"} : undefined)}>{money(row.cpi)}</TableCell>}
            <TableCell align="right">{(row.leadsTotal || 0).toLocaleString()}</TableCell>
            {canShowMonthly && <TableCell align="right" {...keepCell(capSx(isToday, row.dailyCap, row.leadsTotal))}>{(row.dailyCap || 0).toLocaleString()}</TableCell>}
            {canShowMonthly && <TableCell align="right" {...keepCell(monthCapSx(row.monthlyCap, row.monthlyTotal))}>{(row.monthlyCap || 0).toLocaleString()}</TableCell>}
            {isAdmin && canShowMonthly && <TableCell align="right">{(row.monthly || 0).toLocaleString()}</TableCell>}
            {isAdmin && canShowMonthly && <TableCell align="right">{(row.monthlyLb || 0).toLocaleString()}</TableCell>}
            {canShowMonthly && <TableCell align="right">{(row.monthlyTotal || 0).toLocaleString()}</TableCell>}
            {isAdmin && <TableCell align="right">{(row.skipPercent || 0).toFixed(1)} % ({row.skip || 0})</TableCell>}
            {isAdmin && (
                <TableCell>
                    {!row.group && (
                        <IconButton size="small" sx={{p: 0}} onClick={(e) => onMenu?.(row, e.currentTarget)}><MoreVertIcon sx={{fontSize: 18}}/></IconButton>
                    )}
                </TableCell>
            )}
        </StripedTableRow>
    );
});

type BlockProps = {
    row: any;
    open: boolean;
    childRows?: any[];
    isAdmin: boolean;
    canShowMonthly: boolean;
    isToday: boolean;
    onToggle: (key: string) => void;
    onMenu: (row: any, el: HTMLElement) => void;
    onSkip: (hpCampaignId: number) => void;
};

export const PrCampaignBlock = React.memo(function PrCampaignBlock({
    row,
    open,
    childRows,
    isAdmin,
    canShowMonthly,
    isToday,
    onToggle,
    onMenu,
    onSkip,
}: BlockProps) {
    return (
        <>
            <PrCampaignRow
                row={row}
                open={open}
                isAdmin={isAdmin}
                canShowMonthly={canShowMonthly}
                isToday={isToday}
                onToggle={onToggle}
                onMenu={onMenu}
                onSkip={onSkip}
            />
            {open && (childRows || []).map((child: any) => (
                <PrCampaignRow
                    key={child.id}
                    row={child}
                    italic
                    isAdmin={isAdmin}
                    canShowMonthly={canShowMonthly}
                    isToday={isToday}
                    onMenu={onMenu}
                    onSkip={onSkip}
                />
            ))}
        </>
    );
});

export default PrCampaignRow;
